import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'motion/react';
import { Trash, CaretRight, Plus, Check, Warning, WarningCircle } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import authedFetch from '../lib/authedFetch';
import { isAdmin } from '../lib/pipelineConsultor';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
// Papéis semânticos do Care DS v3 (nunca hex), com os nomes do CareOS.
// O resto da tela usa as classes do kit (src/styles/care-ui.css).
const C = {
  canvas: 'var(--care-bg-canvas)',
  surface: 'var(--care-bg-surface)',
  text: 'var(--care-text-primary)',
  text2: 'var(--care-text-secondary)',
  text3: 'var(--care-text-tertiary)',
  border: 'var(--border)',
  borderSubtle: 'var(--care-border-subtle)',
  font: 'var(--care-font-sans)',
};
const NUM = { fontVariantNumeric: 'tabular-nums lining-nums' };

const DAYS = [
  ['seg', 'Seg'], ['ter', 'Ter'], ['qua', 'Qua'],
  ['qui', 'Qui'], ['sex', 'Sex'], ['sab', 'Sáb'],
];

// Campos numéricos do funil que o consultor preenche.
// (Agendamentos, Leads recebidos e Pago foram removidos do formulário a pedido —
// as colunas continuam no banco, só não aparecem mais pra preencher.)
const NUM_FIELDS = [
  ['oi', 'Apresentações', 'leads que viraram apresentação (OI)'],
  ['pc', 'Diagnósticos', 'apresentações que viraram diagnóstico (PC)'],
  ['n', 'Fechamentos', 'negócios fechados (N)'],
];

// Mesmo teto do backend (MAX_LEADS_TEMPERATURA). A tela trava ANTES de salvar:
// cortar no backend depois do "Salvo!" faria o consultor perder linha sem saber.
const MAX_LEADS = 60;

// Temperatura do lead. Os valores gravados são minúsculos e fechados
// (o backend descarta qualquer coisa fora de frio/morno/quente).
const TEMPS = [
  ['frio', 'Frio'],
  ['morno', 'Morno'],
  ['quente', 'Quente'],
];

const emptyManual = () => ({
  leads_recebidos: 0, pago: 0, agendamentos: 0, oi: 0, pc: 0, n: 0,
  auc_captado: 0, fee_acordado: '', recomendacoes: '',
  dia_a_dia: { seg: '', ter: '', qua: '', qui: '', sex: '', sab: '' },
  captacoes: [],
  leads_temperatura: [],
});

// Converte valor pt-BR em número: aceita "2.500.000", "2500000", "1,5", "R$ 1.234,56".
// Regra: remove pontos de milhar, vírgula vira ponto decimal. Negativo vira 0.
const parseBRL = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? Math.max(0, v) : 0;
  const str = String(v).replace(/[R$\s]/g, '');
  if (!str) return 0;
  const n = parseFloat(str.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};
// Formatado pra exibir no input no blur (sem "R$", preserva decimais)
const fmtInput = (v) => {
  const n = parseBRL(v);
  return n ? n.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '';
};
// Cru pra editar no focus ("2500000" ou "1,5")
const rawInput = (v) => {
  const n = parseBRL(v);
  return n ? String(n).replace('.', ',') : '';
};
const sumCaps = (caps) => (caps || []).reduce((t, c) => t + parseBRL(c.auc), 0);
// AUC é inteiro — sem casas decimais variáveis ("R$ 1.000" vs "R$ 1.000,5")
const fmtBRL = (v) => 'R$ ' + (Number(v) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

function MinhaSemana() {
  const { user, logout } = useAuth();
  const admin = isAdmin(user);          // admin → modo "ver como consultor" (leitura)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [edits, setEdits] = useState({});       // weekNumber -> manual
  const [savingWeek, setSavingWeek] = useState(null);
  const [savedWeek, setSavedWeek] = useState(null);
  const [consultoresList, setConsultoresList] = useState([]);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [adminEditing, setAdminEditing] = useState(false);  // admin pode preencher no lugar do consultor
  const [saveError, setSaveError] = useState(null);         // { week, msg, detalhe } — erro de salvamento inline no card

  const readOnly = admin && !adminEditing;  // admin: leitura por padrão; editável ao ligar "Editar"

  // Semana-alvo vinda do link do e-mail (?year=&week=): abre a semana do e-mail
  // (não a atual). O e-mail é da semana N; o consultor costuma clicar na segunda
  // seguinte (já N+1) — sem isto os números iam pro bucket errado.
  const urlTarget = React.useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const w = parseInt(p.get('week'), 10);
    const y = parseInt(p.get('year'), 10);
    return { week: Number.isFinite(w) ? w : null, year: Number.isFinite(y) ? y : null };
  }, []);

  // Admin: carrega a lista de consultores pro seletor
  useEffect(() => {
    if (!admin) return;
    authedFetch(`${BACKEND}/api/minha-semana/admin/consultores`)
      .then((r) => (r.ok ? r.json() : { consultores: [] }))
      .then((j) => {
        const lista = j.consultores || [];
        setConsultoresList(lista);
        const pre = new URLSearchParams(window.location.search).get('preview');
        setPreviewEmail(pre || lista[0]?.email || null);
      })
      .catch(() => {});
  }, [admin]);

  // Admin: ao trocar de consultor no seletor OU alternar o modo edição, descarta
  // edits pendentes (keyed por week, não por consultor) e fecha a semana aberta —
  // senão os números digitados pro consultor A seriam gravados na linha do B.
  useEffect(() => {
    setEdits({});
    setExpanded(null);
    setSavedWeek(null);
    setSaveError(null);
  }, [previewEmail, adminEditing]);

  const load = useCallback(async () => {
    if (admin && !previewEmail) { setLoading(false); return; }  // espera a seleção
    setLoading(true);
    setError(null);
    try {
      const yq = urlTarget.year ? `year=${urlTarget.year}` : '';
      const url = admin
        ? `${BACKEND}/api/minha-semana/admin/preview?consultor_email=${encodeURIComponent(previewEmail)}${yq ? `&${yq}` : ''}`
        : `${BACKEND}/api/minha-semana/semanas${yq ? `?${yq}` : ''}`;
      const res = await authedFetch(url);
      if (res.status === 403) { setForbidden(true); setLoading(false); return; }
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const json = await res.json();
      setData(json);
      if (json.semanas && json.semanas.length) {
        // Se o link do e-mail carimbou a semana (?week=), abre ELA; senão a atual.
        const alvo = urlTarget.week && json.semanas.find((s) => s.week === urlTarget.week);
        setExpanded((alvo || json.semanas[0]).week);
      }
    } catch (e) {
      setError(e.message || 'Falha ao carregar');
    } finally {
      setLoading(false);
    }
  }, [admin, previewEmail, urlTarget.year, urlTarget.week]);

  useEffect(() => { load(); }, [load]);

  const getEdit = (semana) => edits[semana.week] || semana.manual || emptyManual();

  const openWeek = (semana) => {
    setSavedWeek(null);
    if (expanded === semana.week) { setExpanded(null); return; }
    if (!edits[semana.week]) {
      // Seed: cada captação ganha id estável (key do React) e AUC formatado pt-BR
      const seeded = { ...emptyManual(), ...semana.manual };
      seeded.captacoes = (seeded.captacoes || []).map((c) => ({
        ...c,
        id: c.id || crypto.randomUUID(),
        auc: fmtInput(c.auc),
      }));
      seeded.leads_temperatura = (seeded.leads_temperatura || []).map((t) => ({
        ...t,
        id: t.id || crypto.randomUUID(),
      }));
      setEdits((prev) => ({ ...prev, [semana.week]: seeded }));
    }
    setExpanded(semana.week);
  };

  const setField = (week, field, value) => {
    setEdits((prev) => ({ ...prev, [week]: { ...prev[week], [field]: value } }));
  };
  const setDia = (week, key, value) => {
    setEdits((prev) => ({
      ...prev,
      [week]: { ...prev[week], dia_a_dia: { ...prev[week].dia_a_dia, [key]: value } },
    }));
  };
  const addCap = (week) => {
    setEdits((prev) => ({
      ...prev,
      [week]: { ...prev[week], captacoes: [...(prev[week]?.captacoes || []), { id: crypto.randomUUID(), cliente: '', auc: '', fee: '' }] },
    }));
  };
  const setCap = (week, idx, field, value) => {
    setEdits((prev) => {
      const caps = [...(prev[week]?.captacoes || [])];
      caps[idx] = { ...caps[idx], [field]: value };
      return { ...prev, [week]: { ...prev[week], captacoes: caps } };
    });
  };
  const removeCap = (week, idx) => {
    setEdits((prev) => {
      const caps = [...(prev[week]?.captacoes || [])];
      caps.splice(idx, 1);
      return { ...prev, [week]: { ...prev[week], captacoes: caps } };
    });
  };

  // Nomes repetidos na mesma semana: o backend unifica (senão o painel contaria
  // duas vezes). Aqui só marcamos pro consultor perceber antes de salvar.
  const nomesDuplicados = (lista) => {
    const vistos = new Map();
    (lista || []).forEach((t) => {
      const k = (t.nome || '').trim().toLowerCase();
      if (k) vistos.set(k, (vistos.get(k) || 0) + 1);
    });
    return vistos;
  };

  const addLeadTemp = (week) => {
    setEdits((prev) => ({
      ...prev,
      [week]: {
        ...prev[week],
        leads_temperatura: [...(prev[week]?.leads_temperatura || []), { id: crypto.randomUUID(), nome: '', temperatura: '' }],
      },
    }));
  };
  const setLeadTemp = (week, idx, field, value) => {
    setEdits((prev) => {
      const lst = [...(prev[week]?.leads_temperatura || [])];
      lst[idx] = { ...lst[idx], [field]: value };
      return { ...prev, [week]: { ...prev[week], leads_temperatura: lst } };
    });
  };
  const removeLeadTemp = (week, idx) => {
    setEdits((prev) => {
      const lst = [...(prev[week]?.leads_temperatura || [])];
      lst.splice(idx, 1);
      return { ...prev, [week]: { ...prev[week], leads_temperatura: lst } };
    });
  };

  const save = async (semana) => {
    const m = getEdit(semana);
    setSavingWeek(semana.week);
    setSavedWeek(null);
    setSaveError(null);
    try {
      const captacoes = (m.captacoes || [])
        .filter((c) => parseBRL(c.auc) > 0 || (c.cliente || '').trim() || (c.fee || '').trim())
        .map((c) => ({ cliente: (c.cliente || '').trim(), auc: parseBRL(c.auc), fee: (c.fee || '').trim() }));
      const body = {
        year: data.year,
        week: semana.week,
        leads_recebidos: Math.max(0, parseInt(m.leads_recebidos) || 0),
        pago: Math.max(0, parseInt(m.pago) || 0),
        agendamentos: Math.max(0, parseInt(m.agendamentos) || 0),
        oi: Math.max(0, parseInt(m.oi) || 0),
        pc: Math.max(0, parseInt(m.pc) || 0),
        n: Math.max(0, parseInt(m.n) || 0),
        recomendacoes: m.recomendacoes || '',
        dia_a_dia: m.dia_a_dia || {},
        captacoes,
        // Linha sem nome é descarte (o backend também filtra); o `id` é só key do React.
        leads_temperatura: (m.leads_temperatura || [])
          .filter((t) => (t.nome || '').trim())
          .map((t) => ({ nome: (t.nome || '').trim(), temperatura: t.temperatura || '' })),
      };
      // Sem captações item a item: reenvia o auc_captado que a API devolveu
      // (valor manual legado da semana), pra não zerar captação antiga no save.
      if (captacoes.length === 0) {
        body.auc_captado = parseBRL(m.auc_captado);
        body.fee_acordado = m.fee_acordado || '';
      }
      const url = admin
        ? `${BACKEND}/api/minha-semana/admin/semana?consultor_email=${encodeURIComponent(previewEmail)}`
        : `${BACKEND}/api/minha-semana/semana`;
      const res = await authedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      setSavedWeek(semana.week);
      // marca como preenchida localmente
      setData((prev) => ({
        ...prev,
        semanas: prev.semanas.map((s) =>
          s.week === semana.week ? { ...s, preenchido: true, manual: body } : s),
      }));
    } catch (e) {
      // Erro inline no card, PERSISTENTE (DS §6 error): frase + "Ver detalhe".
      // Some só no próximo save() ou ao trocar de consultor/modo.
      setSaveError({ week: semana.week, msg: 'Não consegui salvar esta semana.', detalhe: e.message || 'erro' });
    } finally {
      setSavingWeek(null);
    }
  };

  // ---------- render ----------
  if (loading) {
    // Loading = skeleton na forma final (topo + título + subtítulo + cards de semana).
    return (
      <div className="ms-form" style={pageWrap} role="status" aria-busy="true" aria-label="Carregando suas semanas">
        <style>{CSS}</style>
        <div style={faixaTopo}>
          <div className="ms-topo ui-trilho" data-entre>
            <div className="skel" style={{ width: 72, height: 11 }} />
            <div className="skel" style={{ width: 96, height: 13 }} />
          </div>
        </div>
        <div className="ui-pagina" style={{ maxWidth: 640 }}>
          <div className="ui-tela">
            <div className="ui-tela__texto">
              <div className="skel" style={{ width: 200, height: 30 }} />
              <div className="skel" style={{ width: '100%', height: 13 }} />
              <div className="skel" style={{ width: '70%', height: 13 }} />
            </div>
          </div>
          {[0, 1, 2].map((n) => (
            <div key={n} className="ui-card" style={{ ...cardSemana, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', minHeight: 56, padding: 'var(--care-space-5) var(--care-space-6)' }}>
              <div className="skel" style={{ width: 160, height: 15 }} />
              <div className="skel" style={{ width: 88, height: 20 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (forbidden) {
    return (
      <Centered>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <h1 className="ui-titulo" data-tamanho="lg">Acesso restrito</h1>
          <p className="ui-tela__sub" style={{ marginTop: 'var(--care-space-4)' }}>
            Esta página é exclusiva dos consultores do pipeline. Se você deveria ter acesso,
            avise o time para cadastrar seu e-mail.
          </p>
          <button type="button" onClick={logout} className="ui-botao" data-variante="fio" style={{ marginTop: 'var(--care-space-6)' }}>Sair</button>
        </div>
      </Centered>
    );
  }
  if (error) {
    return (
      <Centered>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <p style={{ color: C.text, fontSize: 'var(--care-text-base)', lineHeight: 1.55, margin: 0 }} role="alert">
            Não foi possível carregar suas semanas agora.
          </p>
          <details className="ui-detalhe" style={{ textAlign: 'left' }}>
            <summary className="ui-detalhe__resumo">
              <span className="ui-detalhe__chevron"><CaretRight size={16} weight="bold" aria-hidden="true" /></span>
              Ver detalhe
            </summary>
            <div className="ui-detalhe__corpo">{error}</div>
          </details>
          <button type="button" onClick={load} className="ui-botao" data-variante="fio" style={{ marginTop: 'var(--care-space-6)' }}>Tentar de novo</button>
        </div>
      </Centered>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
    <div className="ms-form" style={pageWrap}>
      <style>{CSS}</style>
      {/* topo */}
      <div style={faixaTopo}>
        <div className="ms-topo ui-trilho" data-entre>
          <div className="ui-eyebrow" data-tom="acento">Care MFO</div>
          <div className="ui-trilho" data-gap="4">
            <span className="ui-rotulo" data-tom="forte">{data?.consultor}</span>
            <button type="button" onClick={logout} className="ui-botao" data-variante="fantasma" data-tamanho="sm" style={{ marginRight: 'calc(-1 * var(--care-space-4))' }}>Sair</button>
          </div>
        </div>
      </div>

      {/* Admin: faixa neutra; o modo vai na pílula + seletor "ver como consultor" */}
      {admin && (
        <div style={faixaTopo}>
          <div className="ms-faixa ui-trilho" data-gap="5">
            <span className="ui-pilula" data-tom={adminEditing ? 'atencao' : 'forte'}>
              {adminEditing ? 'Modo edição' : 'Somente leitura'}
            </span>
            <label htmlFor="ms-consultor" className="ui-rotulo">
              {adminEditing ? 'Preenchendo no lugar de' : 'Ver como'}
            </label>
            <select
              id="ms-consultor"
              className="ui-campo"
              style={{ maxWidth: '100%' }}
              value={previewEmail || ''}
              onChange={(e) => setPreviewEmail(e.target.value)}
            >
              {consultoresList.map((c) => (
                <option key={c.email} value={c.email}>{c.nome} ({c.email})</option>
              ))}
            </select>
            <button
              type="button"
              className="ui-botao"
              data-variante="fio"
              onClick={() => setAdminEditing((v) => !v)}
              disabled={!previewEmail}
            >
              {adminEditing ? 'Sair da edição' : 'Editar'}
            </button>
          </div>
        </div>
      )}

      <main className="ui-pagina" style={{ maxWidth: 640 }}>
        <header className="ui-tela">
          <div className="ui-tela__texto">
            <h1 className="ui-tela__titulo">Minha Semana</h1>
            <p className="ui-tela__sub">
              {readOnly
                ? 'Visualização das semanas deste consultor. As reuniões vêm do tl;dv; os campos são o que ele preenche. Você não altera nada aqui.'
                : 'Registre como foi cada semana. Você pode preencher semanas anteriores que esqueceu. O que você salva aqui alimenta o pipeline do painel.'}
            </p>
          </div>
        </header>

        {data?.semanas?.length === 0 && (
          <div className="ui-card">
            <div className="ui-vazio-bloco" data-area>
              <p className="ui-vazio">Nenhuma semana disponível.</p>
            </div>
          </div>
        )}

        {data?.semanas?.map((s) => {
          const isOpen = expanded === s.week;
          const m = getEdit(s);
          const dupsSemana = nomesDuplicados(m.leads_temperatura);
          const temDuplicado = Array.from(dupsSemana.values()).some((qtd) => qtd > 1);
          const totalLeads = (m.leads_temperatura || []).length;
          const salvando = savingWeek === s.week;
          return (
            <div key={s.week} className="ui-card" style={cardSemana}>
              {/* header da semana */}
              <button
                type="button"
                id={`ms-sem-btn-${s.week}`}
                onClick={() => openWeek(s)}
                className="ms-week"
                aria-expanded={isOpen}
                aria-controls={`ms-sem-${s.week}`}
              >
                <span className="ui-trilho" data-gap="5">
                  <span className="ui-titulo" data-tamanho="sm">Semana {s.week}</span>
                  <span style={{ color: C.text2, fontSize: 'var(--care-text-sm)', ...NUM }}>{s.week_range}</span>
                  {s.is_current && <span className="ui-pilula" data-tom="acento">atual</span>}
                </span>
                <span className="ui-trilho" data-gap="4" data-nowrap>
                  {s.preenchido
                    ? <span className="ui-pilula" data-tom="positivo">preenchida</span>
                    : <span className="ui-pilula" data-tom="atencao">pendente</span>}
                  <span className="ms-chevron" data-aberto={isOpen} aria-hidden="true">
                    <CaretRight size={20} />
                  </span>
                </span>
              </button>

              <AnimatePresence>
              {isOpen && (
                <motion.div
                  id={`ms-sem-${s.week}`}
                  role="region"
                  aria-labelledby={`ms-sem-btn-${s.week}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.28, ease: [0.2, 0, 0, 1] }}
                  style={{ padding: 'var(--care-space-2) var(--care-space-6) var(--care-space-7)' }}
                >
                  {/* referência do sistema */}
                  <div style={refBox}>
                    <div className="ui-rotulo" data-tom="forte" style={{ marginBottom: 'var(--care-space-3)' }}>
                      O sistema já contou nesta semana:
                    </div>
                    <div style={{ fontSize: 'var(--care-text-sm)', color: C.text2, ...NUM }}>
                      {s.auto.leads} lead(s) · {s.auto.agendamentos} apresentação(ões) registrada(s) no tl;dv
                    </div>
                    {s.reunioes?.length > 0 && (
                      <ul className="ui-lista" data-tom="neutro" style={{ marginTop: 'var(--care-space-4)' }}>
                        {s.reunioes.map((r, i) => (
                          <li key={i}>
                            {(r.participantes && r.participantes[0]?.nome) || r.name || 'Apresentação'}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* campos numéricos */}
                  <div style={grid2}>
                    {NUM_FIELDS.map(([key, label, hint]) => (
                      <div key={key}>
                        <label htmlFor={`ms-${s.week}-${key}`} className="ui-rotulo" style={lbl}>{label}</label>
                        <input
                          id={`ms-${s.week}-${key}`}
                          type="number" min="0" inputMode="numeric"
                          value={m[key] ?? 0}
                          onChange={(e) => setField(s.week, key, e.target.value)}
                          className="ui-campo"
                          data-numerico
                          style={campo}
                          readOnly={readOnly}
                          aria-readonly={readOnly || undefined}
                        />
                        {hint && <div style={hintStyle}>{hint}</div>}
                      </div>
                    ))}
                  </div>

                  {/* Temperatura dos leads (nome + frio/morno/quente) */}
                  <fieldset className="ms-grupo" style={{ marginTop: 'var(--care-space-6)' }}>
                    <legend className="ui-rotulo" style={lbl}>Temperatura dos leads</legend>
                    {(m.leads_temperatura || []).length === 0 && (
                      <p className="ui-vazio" style={{ margin: '0 0 var(--care-space-2)' }}>
                        Adicione cada lead que você está trabalhando e marque como ele está.
                      </p>
                    )}
                    {/* flexWrap: no celular a linha quebra sozinha em vez de espremer o nome */}
                    {(m.leads_temperatura || []).map((t, idx) => {
                      const dup = (dupsSemana.get((t.nome || '').trim().toLowerCase()) || 0) > 1;
                      return (
                      <div key={t.id || idx} className="ui-trilho" data-gap="4" style={{ marginBottom: 'var(--care-space-4)' }}>
                        <input
                          className="ui-campo"
                          type="text" placeholder="Lead"
                          value={t.nome ?? ''}
                          onChange={(e) => setLeadTemp(s.week, idx, 'nome', e.target.value)}
                          style={{ flex: '1 1 150px', borderColor: dup ? 'var(--care-warning-border)' : undefined }}
                          title={dup ? 'Este lead está repetido — vale uma linha só' : undefined}
                          aria-label="Nome do lead"
                          aria-describedby={dup ? `dup-${s.week}` : undefined}
                          readOnly={readOnly}
                          aria-readonly={readOnly || undefined}
                        />
                        {dup && <span className="ui-pilula" data-tom="atencao">repetido</span>}
                        <div className="ui-segmentado ms-temp" role="group" aria-label={`Temperatura de ${t.nome || 'lead sem nome'}`}>
                          {TEMPS.map(([val, label]) => {
                            const on = t.temperatura === val;
                            return (
                              <button
                                key={val}
                                type="button"
                                className="ui-segmentado__opcao"
                                aria-pressed={on}
                                aria-disabled={readOnly || undefined}
                                /* clicar no que já está marcado desmarca — dá como desfazer um clique errado */
                                onClick={readOnly ? undefined : () => setLeadTemp(s.week, idx, 'temperatura', on ? '' : val)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        {!readOnly && (
                          <button
                            type="button"
                            className="ui-botao"
                            data-variante="fantasma"
                            data-so-icone
                            data-tom="negativo"
                            onClick={() => removeLeadTemp(s.week, idx)}
                            title="Remover"
                            aria-label="Remover lead"
                          ><Trash size={16} weight="bold" aria-hidden="true" /></button>
                        )}
                      </div>
                      );
                    })}
                    {temDuplicado && (
                      <div id={`dup-${s.week}`} className="ui-aviso" data-tom="atencao" style={{ marginBottom: 'var(--care-space-4)' }}>
                        <Warning size={16} weight="bold" aria-hidden="true" />
                        Tem lead repetido. Ao salvar, vale uma linha só: a última que você marcou.
                      </div>
                    )}
                    {!readOnly && (
                      totalLeads >= MAX_LEADS ? (
                        <div className="ui-aviso" data-tom="atencao" style={{ marginTop: 'var(--care-space-2)' }}>
                          <Warning size={16} weight="bold" aria-hidden="true" />
                          Limite de {MAX_LEADS} leads por semana. Remova algum pra adicionar outro.
                        </div>
                      ) : (
                        <button type="button" onClick={() => addLeadTemp(s.week)} className="ui-botao" data-variante="fantasma" data-tamanho="sm" style={botaoAdicionar}>
                          <Plus size={16} weight="bold" aria-hidden="true" />Adicionar lead
                        </button>
                      )
                    )}
                  </fieldset>

                  {/* Captações da semana (cliente + AUC + fee, uma por uma) */}
                  <fieldset className="ms-grupo" style={{ marginTop: 'var(--care-space-6)' }}>
                    <legend className="ui-rotulo" style={lbl}>Captações da semana</legend>
                    {(m.captacoes || []).length === 0 && (
                      <p className="ui-vazio" style={{ margin: '0 0 var(--care-space-2)' }}>
                        Adicione cada cliente captado com seu valor e fee. O total soma sozinho.
                      </p>
                    )}
                    {(m.captacoes || []).map((c, idx) => (
                      <div key={c.id || idx} className="ms-cap">
                        <input
                          className="ui-campo ms-cap-cliente"
                          type="text" placeholder="Cliente"
                          value={c.cliente ?? ''}
                          onChange={(e) => setCap(s.week, idx, 'cliente', e.target.value)}
                          style={campo}
                          aria-label="Cliente captado"
                          readOnly={readOnly}
                          aria-readonly={readOnly || undefined}
                        />
                        {/* AUC como texto pt-BR: aceita "2.500.000" e "1,5"; cru no focus, formatado no blur */}
                        <input
                          className="ui-campo"
                          data-numerico
                          type="text" inputMode="decimal" placeholder="AUC R$"
                          value={c.auc ?? ''}
                          onChange={(e) => setCap(s.week, idx, 'auc', e.target.value)}
                          onFocus={() => { if (readOnly) return; setCap(s.week, idx, 'auc', rawInput(c.auc)); }}
                          onBlur={() => { if (readOnly) return; setCap(s.week, idx, 'auc', fmtInput(c.auc)); }}
                          style={campo}
                          aria-label="AUC captado em reais"
                          readOnly={readOnly}
                          aria-readonly={readOnly || undefined}
                        />
                        <input
                          className="ui-campo"
                          data-numerico
                          type="text" placeholder="Fee"
                          value={c.fee ?? ''}
                          onChange={(e) => setCap(s.week, idx, 'fee', e.target.value)}
                          style={campo}
                          aria-label="Fee acordado"
                          readOnly={readOnly}
                          aria-readonly={readOnly || undefined}
                        />
                        {!readOnly && (
                          <button
                            type="button"
                            className="ui-botao"
                            data-variante="fantasma"
                            data-so-icone
                            data-tom="negativo"
                            onClick={() => removeCap(s.week, idx)}
                            title="Remover"
                            aria-label="Remover captação"
                          ><Trash size={16} weight="bold" aria-hidden="true" /></button>
                        )}
                      </div>
                    ))}
                    {!readOnly && (
                      <button type="button" onClick={() => addCap(s.week)} className="ui-botao" data-variante="fantasma" data-tamanho="sm" style={botaoAdicionar}>
                        <Plus size={16} weight="bold" aria-hidden="true" />Adicionar captação
                      </button>
                    )}
                    <hr className="ui-fio" style={{ marginTop: 'var(--care-space-5)' }} />
                    <div className="ui-trilho" data-entre data-alinhar="base" style={{ paddingTop: 'var(--care-space-5)' }}>
                      <span className="ui-rotulo">Total captado na semana</span>
                      <span className="ui-kpi" data-tamanho="sm">
                        <span className="ui-kpi__valor">{fmtBRL(sumCaps(m.captacoes))}</span>
                      </span>
                    </div>
                  </fieldset>

                  {/* recomendações */}
                  <div style={{ marginTop: 'var(--care-space-6)' }}>
                    <label htmlFor={`ms-${s.week}-recomendacoes`} className="ui-rotulo" style={lbl}>Recomendações</label>
                    <textarea
                      id={`ms-${s.week}-recomendacoes`}
                      className="ui-campo ui-campo--area"
                      rows={2}
                      value={m.recomendacoes ?? ''}
                      onChange={(e) => setField(s.week, 'recomendacoes', e.target.value)}
                      style={campo}
                      placeholder="recomendações que você gerou na semana"
                      readOnly={readOnly}
                      aria-readonly={readOnly || undefined}
                    />
                  </div>

                  {/* dia a dia */}
                  <fieldset className="ms-grupo" style={{ marginTop: 'var(--care-space-6)' }}>
                    <legend className="ui-rotulo" style={lbl}>Como foi cada dia</legend>
                    <div className="ms-days">
                      {DAYS.map(([key, label]) => (
                        <div key={key}>
                          <label htmlFor={`ms-${s.week}-${key}`} className="ui-rotulo" style={{ display: 'block', marginBottom: 'var(--care-space-1)' }}>{label}</label>
                          <input
                            id={`ms-${s.week}-${key}`}
                            className="ui-campo"
                            type="text"
                            value={m.dia_a_dia?.[key] ?? ''}
                            onChange={(e) => setDia(s.week, key, e.target.value)}
                            style={campo}
                            readOnly={readOnly}
                            aria-readonly={readOnly || undefined}
                          />
                        </div>
                      ))}
                    </div>
                  </fieldset>

                  {/* salvar (oculto no modo leitura/preview admin) */}
                  {!readOnly && (
                    <div style={{ marginTop: 'var(--care-space-6)' }}>
                      <div className="ui-trilho" data-gap="5">
                        <button
                          type="button"
                          className="ui-botao"
                          data-variante="solido"
                          onClick={() => save(s)}
                          disabled={salvando}
                          aria-busy={salvando}
                        >
                          {salvando ? 'Salvando…' : 'Salvar semana'}
                        </button>
                        {savedWeek === s.week && (
                          <span role="status" className="ui-aviso" data-tom="positivo">
                            <Check size={16} weight="bold" aria-hidden="true" />Salvo
                          </span>
                        )}
                      </div>
                      {saveError?.week === s.week && (
                        <div style={{ marginTop: 'var(--care-space-5)' }}>
                          <div className="ui-aviso" role="alert" style={{ color: 'var(--destructive)' }}>
                            <WarningCircle size={16} weight="bold" aria-hidden="true" />
                            {saveError.msg}
                          </div>
                          <details className="ui-detalhe">
                            <summary className="ui-detalhe__resumo">
                              <span className="ui-detalhe__chevron"><CaretRight size={16} weight="bold" aria-hidden="true" /></span>
                              Ver detalhe
                            </summary>
                            <div className="ui-detalhe__corpo">{saveError.detalhe}</div>
                          </details>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
              </AnimatePresence>
            </div>
          );
        })}
      </main>
    </div>
    </MotionConfig>
  );
}

// ---------- estilos ----------
// O visual vem do kit do CareOS (src/styles/care-ui.css). Aqui só o que é
// próprio desta tela (grades, cabeçalho de semana, chevron, grupos), com
// tokens --care-* e motion do DS. Foco: regra global :focus-visible do index.css.
const CSS = `
.ms-topo, .ms-faixa { max-width: 640px; margin: 0 auto; box-sizing: border-box; }
.ms-topo { padding: var(--care-space-6) var(--care-space-7); }
.ms-faixa { padding: var(--care-space-5) var(--care-space-7); }
.ms-cap { display: grid; gap: var(--care-space-4); margin-bottom: var(--care-space-4); align-items: center;
  grid-template-columns: minmax(0, 1fr) 136px 80px 40px; }
.ms-days { display: grid; gap: var(--care-space-4); grid-template-columns: 1fr 1fr 1fr; }
.ms-week { width: 100%; display: flex; justify-content: space-between; align-items: center; gap: var(--care-space-5);
  padding: var(--care-space-5) var(--care-space-6); min-height: 56px; border: 0; background: transparent;
  cursor: pointer; text-align: left; font-family: inherit; color: inherit;
  border-radius: var(--radius-card) var(--radius-card) 0 0;
  transition: background-color var(--care-duration-fast) var(--care-ease-standard); }
.ms-week[aria-expanded=false] { border-radius: var(--radius-card); }
.ms-week:hover { background: var(--secondary); }
.ms-week:active { background: var(--care-state-pressed-overlay); }
.ms-week:focus-visible { outline: 2px solid var(--care-border-focus); outline-offset: -2px; }
.ms-chevron { display: inline-flex; color: var(--care-text-tertiary);
  transition: transform var(--care-duration-fast) var(--care-ease-standard); }
.ms-chevron[data-aberto=true] { transform: rotate(90deg); }
.ms-grupo { border: 0; padding: 0; margin: 0; min-width: 0; }
.ms-temp .ui-segmentado__opcao[aria-disabled=true] { cursor: default; }
.ms-temp .ui-segmentado__opcao[aria-disabled=true]:hover:not([aria-pressed=true]) { color: var(--muted-foreground); }
.ms-temp .ui-segmentado__opcao[aria-disabled=true]:active:not([aria-pressed=true]) { background: transparent; }
/* modo leitura: campo sem o hover de "posso editar" */
.ms-form .ui-campo[readonly]:hover { border-color: var(--input); }
@media (max-width: 600px) {
  .ms-topo, .ms-faixa { padding-left: var(--care-space-6); padding-right: var(--care-space-6); }
  /* anti-zoom do iOS: campo abaixo de 16px faz o Safari dar zoom ao focar */
  .ms-form .ui-campo { font-size: var(--care-text-md); }
  /* alvo de toque no celular (DS §6) */
  .ms-temp .ui-segmentado__opcao { min-height: var(--care-control-height); }
}
@media (max-width: 480px) {
  .ms-cap { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 40px; }
  .ms-cap .ms-cap-cliente { grid-column: 1 / -1; }
  .ms-days { grid-template-columns: 1fr 1fr; }
}
`;
const pageWrap = { minHeight: '100vh', background: C.canvas, fontFamily: C.font };
const faixaTopo = { background: C.surface, borderBottom: `1px solid ${C.border}` };
const Centered = ({ children }) => (
  <div style={{ minHeight: '100vh', background: C.canvas, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: C.font, color: C.text2, padding: 'var(--care-space-7)' }}>
    <style>{CSS}</style>
    {children}
  </div>
);
const cardSemana = { padding: 0, gap: 0, marginBottom: 'var(--care-space-5)' };
const refBox = { background: 'var(--muted)', border: `1px solid ${C.borderSubtle}`, borderRadius: 'var(--radius)', padding: 'var(--care-space-5)', marginBottom: 'var(--care-space-6)' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--care-space-5)' };
const lbl = { display: 'block', padding: 0, marginBottom: 'var(--care-space-2)' };
const campo = { width: '100%' };
const hintStyle = { fontSize: 'var(--care-text-xs)', color: C.text3, marginTop: 'var(--care-space-1)' };
// Botão "Adicionar …" alinhado à borda do conteúdo (compensa o padding do .ui-botao sm).
const botaoAdicionar = { marginTop: 'var(--care-space-2)', marginLeft: 'calc(-1 * var(--care-space-4))' };

export default MinhaSemana;
